import { AUDIT_BASE_URL } from "../../utils/ApiUrls";

const AUDIT_PLAN_BASE_URL = `${AUDIT_BASE_URL}/plans`

class AuditPlanService {
  /**
   * Create a new audit plan
   * @param {Object} planData - The audit plan data
   * @param {string} accessToken - JWT access token
   */
  async createAuditPlan(planData, accessToken) {
    try {
      // Transform frontend data to backend format
      const backendData = {
        title: planData.title,
        objectives: planData.objectives,
        scope: planData.scope,
        reference: planData.reference,
        location: planData.location,
        note: planData.note,
        audit_program: planData.program_id,
        parties: planData.parties.map(p => p.id), // Array of audit program party IDs
        risks: planData.risks.map(r => ({
          id: r.id,
          probability: r.probability
        }))
      };

      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(backendData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit plan creation failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  
  // === ATTACHMENT METHODS ===

  /**
   * Upload an attachment to an audit plan
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   * @param {File} file - The file to upload
   * @param {string} description - Optional description for the attachment
   */
  async uploadAttachment(accessToken, planId, file, description = '') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (description) {
        formData.append('description', description);
      }

      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/attachments/upload/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Attachment upload failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Delete an attachment from an audit plan
   * @param {string} accessToken - JWT access token
   * @param {number} attachmentId - The attachment ID to delete
   */
  async deleteAttachment(accessToken, attachmentId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/plan-attachments/${attachmentId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Attachment deletion failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Download an audit plan attachment
   * @param {string} accessToken - JWT access token
   * @param {number} attachmentId - The attachment ID to download
   */
  async downloadAttachment(accessToken, attachmentId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/plan-attachments/${attachmentId}/download/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.detail || 'Failed to download attachment'
        };
      }

      // Get the filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `attachment_${attachmentId}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      // Get the blob
      const blob = await response.blob();
      
      // Create a download link and trigger it
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error downloading attachment:', error);
      return {
        success: false,
        error: error.message || 'An error occurred while downloading the attachment'
      };
    }
  }


  /**
   * Fetch all audit plans for the organization
   * @param {string} accessToken - JWT access token
   * @param {Object} filters - Filter options
   * @param {Object} paginationData - Pagination options
   */
  async getAuditPlans(accessToken, filters = {}, paginationData = {}) {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.title) {
        queryParams.append('title', filters.title);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.program) {
        queryParams.append('program', filters.program);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }

      if (paginationData.page) {
        queryParams.append('page', paginationData.page);
      }

      if (paginationData.page_size) {
        queryParams.append('page_size', paginationData.page_size);
      }

      const queryString = queryParams.toString();
      const url = `${AUDIT_PLAN_BASE_URL}/${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching audit plans',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get detailed information about a specific audit plan
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   */
  async getAuditPlanDetail(accessToken, planId) {
    try {
      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching audit plan details',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Update an existing audit plan
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   * @param {Object} planData - The updated audit plan data
   * @param {Set} riskTracking - Object with existing and new risk sets
   */
  async updateAuditPlan(accessToken, planId, planData, auditPlanRisksToRemove) {
    try {
      console.log('🔍 AUDIT PLAN UPDATE DEBUG:');
      console.log('Current risks (AuditPlanRisk):', planData.current_risks);
      console.log('New risks to add (Risk):', planData.new_risks_to_add);
      console.log('AuditPlanRisks to remove:', auditPlanRisksToRemove);
      
      // Transform frontend data to backend format
      const backendData = {
        title: planData.title,
        objectives: planData.objectives,
        scope: planData.scope,
        reference: planData.reference,
        location: planData.location,
        note: planData.note,
        // audit_program is intentionally excluded - it cannot be changed during updates
        parties: planData.parties.map(p => p.id), // Array of audit program party IDs
        auditplan_risks_to_remove: auditPlanRisksToRemove, // Array of AuditPlanRisk IDs to remove
        risks_to_add: planData.new_risks_to_add.map(r => r.id), // Array of original Risk IDs to add
        current_risks_updates: planData.current_risks.map(r => ({
          id: r.id,
          probability: r.probability
        })) // Array of current risk updates with id and probability
      };
      
      console.log('📤 Sending to backend:', backendData);

      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/edit/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(backendData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit plan update failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // === WORKFLOW METHODS ===

  /**
   * Release an audit plan
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   */
  async releaseAuditPlan(accessToken, planId) {
    try {
      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/release/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit plan release failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }


    // start audit plan to be done by auditor   
  /**
   * Start an audit plan
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   */
  async startAuditPlan(accessToken, planId) {
    try {
      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/start/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit plan start failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Complete an audit plan
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   */
  async completeAuditPlan(accessToken, planId) {
    try {
      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/complete/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit plan completion failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Publish an audit plan
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   */
  async publishAuditPlan(accessToken, planId) {
    try {
      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/publish/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit plan publish failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }


    /**
   * Pause publication of an audit plan
   * @param {string} accessToken - JWT access token
   * @param {number} planId - Audit plan ID
   */
  async pausePublicationAuditPlan(accessToken, planId) {
    try {
      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/pause-publication/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to pause publication',
          errorData: data,
        };
      }

      return {
        success: true,
        message: data.detail || 'Publication paused successfully',
      };
    } catch (error) {
      console.error('Error pausing publication:', error);
      return {
        success: false,
        error: 'Network error occurred while pausing publication',
      };
    }
  }
  

  /**
   * Close an audit plan
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   */
  async closeAuditPlan(accessToken, planId) {
    try {
      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/close/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit plan close failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Achieve an audit plan
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   */
  async achieveAuditPlan(accessToken, planId) {
    try {
      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/achieve/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit plan achieve failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Unachieve an audit plan (reverse achieve action)
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   */
  async unachieveAuditPlan(accessToken, planId) {
    try {
      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/unachieve/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit plan unachieve failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Reopen an audit plan (change status from closed back to completed)
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   */
  async reopenAuditPlan(accessToken, planId) {
    try {
      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/reopen/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit plan reopen failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Review an audit plan (change status from completed back to in_progress)
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   */
  async reviewAuditPlan(accessToken, planId) {
    try {
      const response = await fetch(`${AUDIT_PLAN_BASE_URL}/${planId}/review/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit plan review failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // === AUDIT USER METHODS - MY AUDIT PLANS ===

  /**
   * Fetch audit plans where the current user is assigned as a party
   * @param {string} accessToken - JWT access token
   * @param {Object} filters - Filter options
   * @param {Object} paginationData - Pagination options
   */
  async getMyAuditPlans(accessToken, filters = {}, paginationData = {}) {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }

      if (paginationData.page) {
        queryParams.append('page', paginationData.page);
      }

      if (paginationData.page_size) {
        queryParams.append('page_size', paginationData.page_size);
      }

      const queryString = queryParams.toString();
      const url = `${AUDIT_BASE_URL}/plans/my-plans/${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching my audit plans',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get detailed information about a specific audit plan where the user is assigned
   * @param {string} accessToken - JWT access token
   * @param {number} planId - The audit plan ID
   */
  async getMyAuditPlanDetail(accessToken, planId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/plans/my-plans/${planId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching my audit plan details',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Search audit plans for dropdowns and filters (for audit managers)
   * @param {string} accessToken - JWT access token
   * @param {string} searchTerm - Search term for filtering
   */
  async searchAuditPlans(accessToken, searchTerm = '') {
    try {
      const queryParams = new URLSearchParams();
      if (searchTerm) {
        queryParams.append('search', searchTerm);
      }

      const queryString = queryParams.toString();
      const url = `${AUDIT_BASE_URL}/plans/search/${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error searching audit plans',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Search audit plans for audit users only audit plans where user is a party
   * @param {string} accessToken - JWT access token
   * @param {string} searchTerm - Search term for filtering
   */
  async searchMyAuditPlans(accessToken, searchTerm = '') {
    try {
      const queryParams = new URLSearchParams();
      if (searchTerm) {
        queryParams.append('search', searchTerm);
      }

      const queryString = queryParams.toString();
      const url = `${AUDIT_BASE_URL}/plans/search-my-plans/${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error searching my audit plans',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Search audit plans for audit users (only plans where user is auditor and plan is released)
   * @param {string} accessToken - JWT access token
   * @param {string} searchTerm - Search term for filtering
   */
  async searchMyAuditPlansAsAuditor(accessToken, searchTerm = '') {
    try {
      const queryParams = new URLSearchParams();
      if (searchTerm) {
        queryParams.append('search', searchTerm);
      }

      const queryString = queryParams.toString();
      const url = `${AUDIT_BASE_URL}/plans/search-my-plans-as-auditor/${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error searching my audit plans',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

//   ---------------------------my-audit plans service---------------------------------------



}

const auditPlanService = new AuditPlanService();
export default auditPlanService;

